import { Box3, Matrix4, Vector3 } from 'three';
import { DEG2RAD, clamp, mapLinear } from 'three/src/math/MathUtils';
import { fromHalfFloat } from "three/src/extras/DataUtils";

export const calcMagSqr = (vec) => [...vec].reduce((prev, x) => prev + (x * x), 0);

export const calcMag = (vec) => Math.sqrt(calcMagSqr(vec));

export const mapClamp = (in0, in1, out0, out1, inT) => {
  const res = mapLinear(inT, in0, in1, out0, out1);
  return clamp(res, out0, out1);
}

// Original from three js:
// https://github.com/mrdoob/three.js/blob/7a08fc465642c535dc6758c30248b78bf2bc7b5c/src/math/Box3.js#L156
const expandByObjectRecurse = (thisBox, object, precise, _vector, _box, _mat) => {
  // Computes the world-axis-aligned bounding box of an object (including its children),
  // accounting for both the object's, and children's, world transforms

  // object.updateWorldMatrix( false, false );

  const geometry = object.geometry;

  if ( geometry !== undefined ) {
    const positionAttribute = geometry.getAttribute( 'position' );
    // precise AABB computation based on vertex data requires at least a position attribute.
    // instancing isn't supported so far and uses the normal (conservative) code path.
    if ( precise === true && positionAttribute !== undefined && object.isInstancedMesh !== true ) {
      
      // Our fix for skinned meshes
      // if (object.isSkinnedMesh) {
      //   object.pose();
      // }

      for ( let i = 0, l = positionAttribute.count; i < l; i ++ ) {
        // Fix if animation will be used
        // Source:
        // https://discourse.threejs.org/t/accurate-gltf-bounding-box/45410/5

        // if (object.isSkinnedMesh) {
        //   _vector.fromBufferAttribute( positionAttribute, i );
        //   object.boneTransform( i, _vector );
        // }
        // else
        if ( object.isMesh === true ) {
          object.getVertexPosition( i, _vector );
        } else {
          _vector.fromBufferAttribute( positionAttribute, i );
        }

        _vector.applyMatrix4(_mat);
        thisBox.expandByPoint( _vector );
      }
    } 
    else {

      if ( object.boundingBox !== undefined ) {

        // object-level bounding box
        if ( object.boundingBox === null ) {
          object.computeBoundingBox();
        }

        _box.copy( object.boundingBox );
      } else {
        // geometry-level bounding box
        if ( geometry.boundingBox === null ) {
          geometry.computeBoundingBox();
        }

        _box.copy(geometry.boundingBox);
      }
      
      _box.applyMatrix4(_mat);

      thisBox.union(_box);
    }
  }

  const children = object.children;
  for ( let i = 0, l = children.length; i < l; i ++ ) {
    const child = children[i];
    const nextMat = new Matrix4().copy(_mat).multiply(child.matrix);;
    expandByObjectRecurse(
      thisBox,
      child,
      precise,
      _vector,
      _box,
      nextMat);
  }

  return thisBox;
}


const boxVerticesIter = (_box, _vector = null) => { 
  _vector = _vector || new Vector3();
  return {
  *[Symbol.iterator]() {
    yield _vector.set(_box.min.x, _box.min.y, _box.min.z);
    yield _vector.set(_box.min.x, _box.min.y, _box.max.z);
    yield _vector.set(_box.min.x, _box.max.y, _box.min.z);
    yield _vector.set(_box.min.x, _box.max.y, _box.max.z);
    yield _vector.set(_box.max.x, _box.min.y, _box.min.z);
    yield _vector.set(_box.max.x, _box.min.y, _box.max.z);
    yield _vector.set(_box.max.x, _box.max.y, _box.min.z);
    yield _vector.set(_box.max.x, _box.max.y, _box.max.z);
  },
}; }


export const meshVerticesIter = (object, precise, _mat) => {
  const _vector = new Vector3();
  const _box = new Box3();
  return {
    *[Symbol.iterator]() {
      const geometry = object.geometry;

      if ( geometry !== undefined ) {
        const positionAttribute = geometry.getAttribute( 'position' );
        // precise AABB computation based on vertex data requires at least a position attribute.
        // instancing isn't supported so far and uses the normal (conservative) code path.
        if ( precise === true && positionAttribute !== undefined && object.isInstancedMesh !== true ) {
          
          // Our fix for skinned meshes
          // if (object.isSkinnedMesh) {
          //   object.pose();
          // }
          for ( let i = 0, l = positionAttribute.count; i < l; i ++ ) {
            // Fix if animation will be used
            // Source:
            // https://discourse.threejs.org/t/accurate-gltf-bounding-box/45410/5
    
            // if (object.isSkinnedMesh) {
            //   _vector.fromBufferAttribute( positionAttribute, i );
            //   object.boneTransform( i, _vector );
            // }
            // else
            if ( object.isMesh === true ) {
              object.getVertexPosition( i, _vector );
            } else {
              _vector.fromBufferAttribute( positionAttribute, i );
            }
    
            _vector.applyMatrix4(_mat);
            yield _vector;
          }
        } 
        else {
    
          if ( object.boundingBox !== undefined ) {
    
            // object-level bounding box
            if ( object.boundingBox === null ) {
              object.computeBoundingBox();
            }
    
            _box.copy( object.boundingBox );
          } else {
            // geometry-level bounding box
            if ( geometry.boundingBox === null ) {
              geometry.computeBoundingBox();
            }
    
            _box.copy(geometry.boundingBox);
          }
          
          _box.applyMatrix4(_mat);
          yield* boxVerticesIter(_box, _vector);
        }
      }

      const children = object.children;
      for ( let i = 0, l = children.length; i < l; i ++ ) {
        const child = children[i];
        const nextMat = new Matrix4().copy(_mat).multiply(child.matrix);;
        yield* meshVerticesIter(child, precise, nextMat);
      }
    
    },}
}


export const getBoundsFromChildren = (object, precise, initMat = null) => {
  const boundsBox = new Box3();
  const _box = new Box3();
  const _vector = new Vector3();
  const nextMat = initMat || new Matrix4().identity();
  expandByObjectRecurse(
    boundsBox,
    object,
    precise,
    _vector,
    _box,
    nextMat
  );
  return boundsBox;
}


export const setScalePosition = (parent, align = null, initMat = null) => {
  const box = getBoundsFromChildren(parent, true, initMat);
  if(box.isEmpty()) {
    return;
  }
  const w = box.max.x - box.min.x
  const h = box.max.y - box.min.y
  const d = box.max.z - box.min.z
  let dimension = Math.max(w, h, d)

  const center = new Vector3()
  box.getCenter(center)
  const hAlign = align ? align[0] * w : 0;
  const vAlign = align ? align[1] * h : 0;
  const dAlign = align ? align[2] * d : 0;
  const deltaCenter = new Vector3(hAlign, vAlign, dAlign);

  parent.position.copy(center).sub(deltaCenter).multiplyScalar(-1 / dimension)
  parent.scale.setScalar(1 / dimension)
  parent.updateMatrix()
  box.min.sub(center).add(deltaCenter).multiplyScalar(1 / dimension)
  box.max.sub(center).add(deltaCenter).multiplyScalar(1 / dimension)
  return box;
}


export const zoomToCanvasRatio = (zoom) => {
  return 0.8 * (10 ** (zoom / 10));
}


export const canvasRatioToZoom = (canvasRatio) => {
  return 10 * Math.log10(canvasRatio / 0.8);
}


export const canvasRatioToCameraPosition = ({
  aspect,
  root,
  initCameraPositionV3,
  cameraTargetPositionV3,
  vFovDeg,
  // The max ratio of the canvas the model can take.
  // Calculated on Horizontal and Vertical independently
  canvasRatio
}) => {
  const upV3 = new Vector3(0, 1, 0);
  const targetToCameraV3 = new Vector3().copy(initCameraPositionV3).sub(cameraTargetPositionV3);
  const cameraDistanceInitial = targetToCameraV3.length();
  const vFovRad = vFovDeg * DEG2RAD;
  const hFovRad = 2 * Math.atan(Math.tan(vFovRad * 0.5) * aspect);

  // In case we want to use refCamera
  // refCamera.current.updateWorldMatrix(true, false);
  // const viewMat = refCamera.current.matrixWorldInverse;
  const viewMat = new Matrix4().lookAt(initCameraPositionV3 ,cameraTargetPositionV3, upV3).setPosition(initCameraPositionV3);
  viewMat.invert();

  let maxCameraDistance = 0;
  const verticalPositionToDeltaCameraDistance = 1 / (canvasRatio * Math.tan(vFovRad * 0.5));
  const horizontalPositionToDeltaCameraDistance = 1 / (canvasRatio * Math.tan(hFovRad * 0.5));

  // Help in debug to do it in 2 step: To world and then to view.
  // for (const vertWorldPosition of meshVerticesIter(root, true, root.matrix)) {
  for (const vertViewPosition of meshVerticesIter(root, true, viewMat)) {
    // const vertViewPosition = refCamera.current.worldToLocal(vertWorldPosition);
    // const vertViewPosition = vertWorldPosition.applyMatrix4(viewMat);
    const dy = Math.abs(vertViewPosition.y) * verticalPositionToDeltaCameraDistance;
    const dx = Math.abs(vertViewPosition.x) * horizontalPositionToDeltaCameraDistance;
    const ddy = dy + vertViewPosition.z;
    const ddx = dx + vertViewPosition.z;
    const cdy = ddy + cameraDistanceInitial;
    const cdx = ddx + cameraDistanceInitial;
    maxCameraDistance = Math.max(maxCameraDistance, cdy, cdx);
  }
  const cameraPositionV3 = new Vector3().copy(targetToCameraV3).normalize().multiplyScalar(maxCameraDistance).add(cameraTargetPositionV3);
  return cameraPositionV3;
}


export const cameraPositionToCanvasRatio = ({
  aspect,
  root,
  cameraPositionV3,
  cameraTargetPositionV3,
  vFovDeg,
}) => {
  const upV3 = new Vector3(0, 1, 0);
  const targetToCameraV3 = new Vector3().copy(cameraPositionV3).sub(cameraTargetPositionV3);
  const cameraDistance = targetToCameraV3.length();
  const vFovRad = vFovDeg * DEG2RAD;
  const hFovRad = 2 * Math.atan(Math.tan(vFovRad * 0.5) * aspect);

  // In case we want to use refCamera
  // refCamera.current.updateWorldMatrix(true, false);
  // const viewMat = refCamera.current.matrixWorldInverse;
  const viewMat = new Matrix4().lookAt(cameraPositionV3 ,cameraTargetPositionV3, upV3).setPosition(cameraPositionV3);
  viewMat.invert();

  let maxHalfAlphaTanVerticalDeg = 0;
  let maxHalfAlphaTanHDeg = 0;
  // Help in debug to do it in 2 step: To world and then to view.
  // for (const vertWorldPosition of meshVerticesIter(root, true, root.matrix)) {
  for (const vertViewPosition of meshVerticesIter(root, true, viewMat)) {
    // const vertViewPosition = refCamera.current.worldToLocal(vertWorldPosition);
    // const vertViewPosition = vertWorldPosition.applyMatrix4(viewMat);
    const halfAlphaTanVerticalDeg = vertViewPosition.y / (vertViewPosition.z);
    if (Number.isFinite(halfAlphaTanVerticalDeg)) {
      maxHalfAlphaTanVerticalDeg = Math.max(maxHalfAlphaTanVerticalDeg, Math.abs(halfAlphaTanVerticalDeg));
    }
    const halfAlphaTanHDeg = vertViewPosition.x / (vertViewPosition.z);
    if (Number.isFinite(halfAlphaTanHDeg)) {
      maxHalfAlphaTanHDeg = Math.max(maxHalfAlphaTanHDeg, Math.abs(halfAlphaTanHDeg));
    }

  }

  const sideV = maxHalfAlphaTanVerticalDeg * cameraDistance;
  const canvasV = cameraDistance * Math.tan(vFovRad * 0.5);
  const ratioV = sideV / canvasV;
  const sideH = maxHalfAlphaTanHDeg * cameraDistance;
  const canvasH = cameraDistance * Math.tan(hFovRad * 0.5);
  const ratioH = sideH / canvasH;
  const ratio = Math.max(ratioV, ratioH);
  return ratio;
}

// HDRI image sampling is based on: https://www.youtube.com/watch?v=5d-qGfc8d80
const calcPixelIntensity = (data, i) => {
  // There is a mistake in the video between r and b
  const b = fromHalfFloat(data[i * 4 + 0]);
  const g = fromHalfFloat(data[i * 4 + 1]);
  const r = fromHalfFloat(data[i * 4 + 2]);
  const a = fromHalfFloat(data[i * 4 + 3]);
  // TODO: Is this correct? Should be sum or something else?
  // In the video intensity is actual the value in HSV and not the physical intensity
  // const intensity = a * Math.max(r, g, b);
  const intensity = a * (0.2126 * r + 0.7152 * g + 0.0722 * b);

  return intensity;
}

/**
 * 
 * @param {Uint16Array} data
 * @param {number} binsCount How many buckets / bins in histogram
 * @param {bool} onlyTopHalf Ignore bottom half of hdri image
 * @returns {number[][]}
 */
export const genHistFromHdriData = (data, binsCount, onlyTopHalf) => {
  const hist = new Array(binsCount);
  for (let i = 0; i < hist.length; i++) {
    hist[i] = [];
  }
  const maxIndex = onlyTopHalf ? (data.length / 4 / 2) : (data.length / 4);

  let intensityMax = calcPixelIntensity(data, 0);
  let intensityMin = intensityMax;
  for (let i = 1; i < maxIndex; i++) {
    const intensity = calcPixelIntensity(data, i);
    intensityMax = Math.max(intensityMax, intensity);
    intensityMin = Math.min(intensityMin, intensity);
  }

  const intensityMaxLog = Math.log10(1 + intensityMax);
  const intensityMinLog = Math.log10(1 + intensityMin);

  for (let i = 0; i < maxIndex; i++) {
    const intensity = calcPixelIntensity(data, i);
    const intensityLog = Math.log10(1 + intensity);
    // const intensityClamped = Math.log10(mapClamp(intensityMin, intensityMax, 1, 10, intensity)) * binsCount;
    const intensityClamped = mapClamp(intensityMinLog, intensityMaxLog, 0, binsCount, intensityLog);
    // const intensityClamped = Math.max(0, Math.min(maxIntensity, maxIntensity * intensity) - 0.0001) / maxIntensity;
    const bin = Math.min(Math.floor(intensityClamped), binsCount - 1);
    if (!hist[bin]) {debugger}
    hist[bin].push(i);
  }
  return hist;
};

/**
 * 
 * @param {number[]} cdf
 * @returns {number} index
 */
export const sampleCdf = (cdf) => {
  let t = Math.random();
  let bin = cdf.length - 1;
  while ((bin > 0) && (t <= cdf[bin - 1])) {
    bin--;
  }

  return bin;
};

/**
 * @template T
 * @param {T[]} vec
 * @returns {T} val
 */
export const sampleUniform = (vec) => {
  const value = vec[Math.floor(Math.random() * vec.length)]
  return value;
};

/**
 * 
 * @param {number} index pixel index from THREE data Uint16Array
 * @param {number} width image width
 * @param {number} height image width
 * @returns {{phi: number, theta: number}} `phi`: range [0, pi] angle in rad from top. `theta`  range [0, 2 * pi] angle in rad rotation along up axis
 */
export const pixelIndexToSphericalCoords = (index, width, height) => {
  const xPixel = index % width;
  const yPixel = Math.floor(index / width);
  const u = xPixel / width; // 0 -> 1
  const v = yPixel / height; // 0 -> 1
  const theta = Math.PI * ((3.5 - 2 * u) % 1);
  const phi = Math.PI * v;
  return {phi, theta};
};

/**
 * 
 * @param {number[]} vec 
 */
const normalizedDist = (vec) => {
  const vecSum = vec.reduce((x,y) => x + y);
  for (let i = 0; i < vec.length; i++) {
    vec[i] /= vecSum;
  }
}

/**
 * 
 * @param {number[][]} hist
 * @param {number} weightPow multiply each value in cdf by Math.pow(i / hist.length, weightPow)
 * @returns {number[]} Normalized weighted cdf
 */
export const genCdfFromHist = (hist, weightPow = 0) => {
  const binsCount = hist.length;
  const pdfWeighted = hist.map((bin, i) => bin.length * Math.pow(i / binsCount, weightPow));
  normalizedDist(pdfWeighted);

  const cdfWeighted = new Array(binsCount);
  cdfWeighted[0] = pdfWeighted[0];
  for (let i = 1; i < cdfWeighted.length; i++) {
    cdfWeighted[i] = cdfWeighted[i - 1] + pdfWeighted[i];
  }
  return cdfWeighted;
}

